import React, { ChangeEvent } from "react";
import isEmpty from "lodash/isEmpty";

import { InputWrapper, Label } from "../../../../../common/styledComponents";
import FormField from "../../../../../common/FormField";
import TextArea from "../../../../../common/TextArea";
import { BusinessAssetTypeSelect } from "../../../../../BusinessAsset/BusinessAssetTypeSelect";
import {
  BusinessAssetFormField,
  IBusinessAssetForm,
  BusinessAssetType,
} from "../../../../../../types";
import { LAND_ASSET_CLASS } from "../../../../../BusinessAsset/immovableAssets";
import {
  isImmovableAsset,
  isIntangibleAsset,
  isCustomAssetClass,
  isNoteRequired,
} from "../../../../../BusinessAsset/utils";
import { BusinessAssetClassSelect } from "@/v1/components/BusinessAsset/BusinessAssetClassSelect";

/**
 * The Update form for business asset.
 * It allows us to modify "assetType", "assetClass", and "depreciationYears".
 */
const BusinessAssetUpdateForm = ({
  businessAssetFormData,
  onFieldChange,
}: IBusinessAssetForm) => {
  const {
    assetType,
    assetClass,
    assetClassCustom,
    note,
    depreciationYears,
    depreciationYearsEditable,
  } = businessAssetFormData;
  const isAssetTypeImmovable = isImmovableAsset(assetType);
  const isAssetTypeIntangible = isIntangibleAsset(assetType);
  const isAssetClassCustom = isCustomAssetClass(assetClass);

  const getOnChangeHandler =
    (field: BusinessAssetFormField) =>
    (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      onFieldChange(field, event.target.value);

  const shouldRenderEditableDepreciationYearsField =
    isAssetTypeImmovable || isAssetTypeIntangible || isAssetClassCustom;

  return (
    <div className="mb-3">
      <InputWrapper>
        <Label htmlFor="assetType">AfA-Klasse</Label>
        <BusinessAssetTypeSelect
          id="assetType"
          value={assetType}
          onChange={(value: BusinessAssetType) =>
            onFieldChange(BusinessAssetFormField.ASSET_TYPE, value)
          }
        />
      </InputWrapper>

      {!isAssetTypeIntangible && (
        <InputWrapper>
          <Label htmlFor="assetClass">Anlage-Typ</Label>
          <BusinessAssetClassSelect
            id="assetClass"
            value={assetClass}
            isAssetTypeImmovable={isAssetTypeImmovable}
            onChange={(value: string) =>
              onFieldChange(BusinessAssetFormField.ASSET_CLASS, value)
            }
          />
        </InputWrapper>
      )}

      {(isAssetTypeIntangible || isAssetClassCustom) && (
        <FormField
          id="assetClassCustom"
          value={assetClassCustom}
          label={isAssetTypeIntangible ? "Anlage-Typ" : "Beschreibung"}
          required
          onChange={getOnChangeHandler(
            BusinessAssetFormField.ASSET_CLASS_CUSTOM
          )}
        />
      )}

      <TextArea
        id="note"
        label="Bezeichnung"
        value={note}
        placeholder="Genaue Beschreibung des Wirtschaftsguts (Marke und Modell)"
        onChange={getOnChangeHandler(BusinessAssetFormField.NOTE)}
        rows={3}
        required={isNoteRequired(assetType, assetClass)}
      />

      {
        // land doesn't depreciate
        assetClass !== LAND_ASSET_CLASS &&
          (depreciationYearsEditable &&
          shouldRenderEditableDepreciationYearsField ? (
            <FormField
              id="depreciationYearsEditable"
              value={depreciationYears}
              type="number"
              label="Abschreibungsdauer"
              textAfter="Jahre"
              required
              onChange={getOnChangeHandler(
                BusinessAssetFormField.DEPRECIATION_YEARS
              )}
            />
          ) : (
            <FormField
              disabled
              className="no-background"
              id="depreciationYears"
              value={
                isEmpty(depreciationYears) ? "" : `${depreciationYears} Jahre`
              }
              label="Abschreibungsdauer"
            />
          ))
      }
    </div>
  );
};

export default BusinessAssetUpdateForm;
